// Updated ComparisonViewContainer.jsx - Using useTranscript hook like TranscriptBodyContainer
import React, { useCallback } from 'react';
import UrlUtil from 'utils/UrlUtil';
import trackingUtil from 'utils/TrackingUtil';
import ComparisonUrlUtil from 'utils/ComparisonUrlUtil';
import InterCoderModal from './InterCoderModal';
import { Redirect } from 'react-router-dom';
import ComparisonView from './ComparisonView';
import { useInitializeParagraphExcerpts } from 'hooks/transcripts/queries'; // Updated to use new hook
import { useTranscript } from 'hooks/transcripts/queries'; // Updated to use new hook
import { useProjectUsers } from 'hooks/users/queries';
import { useTranscriptExcerpts } from 'hooks/transcripts/useTranscriptExcerpts';
import { hasBotCoding } from 'hooks/transcripts/useBotExcerpts';
import PageLoadingComponent from './PageLoadingComponent';
import useDefaultErrorToast from 'hooks/useDefaultErrorToast';
import { BOT_USER_ID, NONE_USER_ID } from 'constants/UserConstants';

const ComparisonViewContainer = (props) => {
  const { data: projectUsers, isLoading: isLoadingUsers } = useProjectUsers(props.projectID);
  const { collaborators = [], owner = {} } = projectUsers || {};
  
  // Use the new React Query hook for transcript data
  const {
    data: transcriptData,
    isLoading: isLoadingTranscript,
    error: transcriptError
  } = useTranscript(props.transcriptID);

  // Initialize paragraph excerpts for the transcript
  useInitializeParagraphExcerpts(props.transcriptID);

  // The page's ONE excerpt resolution set (see useTranscriptExcerpts'
  // mount-once contract): ComparisonView and InterCoderModal consume it as
  // a prop, and the bot check derives from the same array instead of
  // mounting a second observer set for a boolean.
  const { excerpts } = useTranscriptExcerpts(props.transcriptID);
  const hasBotExcerpts = React.useMemo(
    () => excerpts.some(hasBotCoding),
    [excerpts]
  );

  // Show error toast if transcript loading fails (skip 404/403 — handled by TranscriptNav's NotFoundComponent)
  const isNotFoundOrForbidden = transcriptError?.status === 404 || transcriptError?.status === 403;
  useDefaultErrorToast(isNotFoundOrForbidden ? null : transcriptError);

  const onUserSelect = useCallback((userID, key) => {
    trackingUtil.logEvent('switch-user-coding-comparison');
    const urlObject = UrlUtil.addParamsToUrlObject(props.location, {
      [key]: userID
    });
    props.history.push(urlObject);
  }, [props.location, props.history]);

  const onLeftUserSelect = useCallback((userID) => {
    onUserSelect(userID, 'left');
  }, [onUserSelect]);

  const onRightUserSelect = useCallback((userID) => {
    onUserSelect(userID, 'right');
  }, [onUserSelect]);

  const onPage = useCallback((page) => {
    // Page handling if needed
  }, []);

  const getUsers = useCallback((users = []) => {
    const userIds = ComparisonUrlUtil.getUserIds(props.location);
    const leftUserID = ComparisonUrlUtil.parseUserId(userIds.leftUserID);
    const rightUserID = ComparisonUrlUtil.parseUserId(userIds.rightUserID);

    const leftUser = users.find((user) => user.id === leftUserID) || null;
    const rightUser = users.find((user) => user.id === rightUserID) || null;

    return { leftUser, rightUser };
  }, [props.location]);

  // The redirect is one-shot (it rewrites the URL, after which both params
  // read as valid forever), so it must not fire before the transcript has
  // loaded: an unloaded transcript (excerpt_ids not yet known) reads as
  // having no bot excerpts, which sent the AI comparison to the wrong
  // default coder.
  const redirectUrl = isLoadingTranscript ? null : ComparisonUrlUtil.redirectComparisonUrl(
    props.location,
    !isLoadingUsers,
    owner,
    collaborators,
    hasBotExcerpts
  );

  if (redirectUrl) {
    return <Redirect to={redirectUrl} />;
  }

  // Show loading state
  if (isLoadingTranscript || isLoadingUsers) {
    return <PageLoadingComponent />;
  }

  // If there's an error, return null (toast will show the error)
  if (transcriptError) {
    return null;
  }

  // Include bot user in the users array if there are bot excerpts
  const botUser = hasBotExcerpts ? { id: BOT_USER_ID, name: 'AI Assistant', email: 'ai@delvetool.com' } : null;
  const users = [owner, ...collaborators, botUser].filter(Boolean);

  const page = UrlUtil.getPageNumber(props.location);
  const { leftUserID, rightUserID } = ComparisonUrlUtil.getUserIds(props.location);

  // Determine if we're in solo mode (rightUserID is 'none' or falsy)
  const isSoloMode = rightUserID === NONE_USER_ID || !rightUserID;

  return (
    <div style={{ height: "100%" }}>
      <InterCoderModal
        show={props.showInterCoderReliability}
        transcriptID={props.transcriptID}
        excerpts={excerpts}
        onHide={props.onHideInterCoderReliability}
      />
      <ComparisonView
        pageCount={props.pageCount}
        onPage={onPage}
        users={users}
        onLeftUserSelect={onLeftUserSelect}
        onRightUserSelect={onRightUserSelect}
        selectedUsers={getUsers(users)}
        transcriptID={props.transcriptID}
        projectID={props.projectID}
        transcriptData={transcriptData}
        excerpts={excerpts}
        leftUserID={leftUserID}
        rightUserID={isSoloMode ? null : rightUserID} // Pass null for solo mode
        page={page}
        hasEditPermission={props.hasEditPermission}
      />
    </div>
  );
};

export default ComparisonViewContainer;